import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { maLoi } from '../../../lib/errors';
import type { ApiError } from '../../../lib/errors';
import { nguoiDungRepository } from '../../../lib/repositories/nguoi-dung.repository';
import { vaiTroRepository } from '../../../lib/repositories/vai-tro.repository';
import { useToast } from '../../../lib/toast';
import { useTenantCuaToi } from '../../auth/hooks/useTenantCuaToi';
import { KHOA_PHONG_BAN } from '../../co-cau-to-chuc/hooks/usePhongBan';
import { KHOA_VAI_TRO } from '../../vai-tro/hooks/useVaiTro';
import { MOT_TRANG } from '../constants';
import { noiNhanThu } from '../nghiep-vu/thao-tac';
import type {
  EmailNhanThuChanged,
  InviteResent,
  InvitedUser,
  MfaRequiredUsers,
  UserInvite,
  UserPage,
  UserProfile,
  UserStatus,
  UserUpdate,
} from '../types';

/**
 * Khoá cache. Cố ý **không** dùng `['users']` trần để vô hiệu hoá: nhánh đó còn
 * chứa `me/context` (`useNguCanhCuaToi`), tải lại nó là tính lại quyền cả cổng.
 */
export const KHOA_NGUOI_DUNG = {
  danhSach: ['users', 'danh-sach'] as const,
  theoLoc: (status: UserStatus | undefined) => ['users', 'danh-sach', status ?? 'tat-ca'] as const,
  /** Mọi người của tổ chức, đã nối hết trang — **không** nằm dưới `danhSach` (khác hình dữ liệu). */
  tatCa: ['users', 'tat-ca'] as const,
  /** Tập người có cờ bắt buộc xác thực hai lớp riêng (`/users/mfa-required`). */
  batBuocMfa: ['users', 'mfa-required'] as const,
};

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 50;

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Danh sách người dùng của tổ chức, lọc trạng thái ở server, phân trang theo
 * cursor: trang sau nối vào cuối bằng `fetchNextPage` ("Tải thêm").
 */
export function useDanhSachNguoiDung(status: UserStatus | undefined) {
  const query = useInfiniteQuery<
    UserPage,
    ApiError,
    InfiniteData<UserPage, string | undefined>,
    ReturnType<typeof KHOA_NGUOI_DUNG.theoLoc>,
    string | undefined
  >({
    queryKey: KHOA_NGUOI_DUNG.theoLoc(status),
    queryFn: ({ pageParam }) =>
      nguoiDungRepository.danhSach({ limit: MOT_TRANG, cursor: pageParam, status }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: thuLai,
  });
  const dong = query.data?.pages.flatMap((p) => p.items) ?? [];
  return { ...query, dong };
}

/**
 * Mọi người của tổ chức, nối `next_cursor` tới hết — cho chỗ cần chọn trong cả
 * tổ chức (bắt buộc xác thực hai lớp với người được chọn). Tổ chức khoảng 30
 * người (ADR-0013), nên một hai trang là hết.
 */
export function useTatCaNguoiDung(bat = true) {
  return useQuery<UserProfile[], ApiError>({
    queryKey: KHOA_NGUOI_DUNG.tatCa,
    queryFn: async () => {
      const ra: UserProfile[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await nguoiDungRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

/** `GET /users/mfa-required` — ai đang có cờ bắt buộc xác thực hai lớp riêng. */
export function useNguoiBatBuocMfa(bat = true) {
  return useQuery<MfaRequiredUsers, ApiError>({
    queryKey: KHOA_NGUOI_DUNG.batBuocMfa,
    queryFn: () => nguoiDungRepository.batBuocMfa(),
    // Cùng nhịp với chính sách tổ chức: quay lại màn phải thấy bản mới nhất.
    staleTime: 0,
    retry: thuLai,
    enabled: bat,
  });
}

/**
 * Cờ bắt buộc xác thực hai lớp riêng vừa đổi hàng loạt (lưu ở bộ chọn ba mức
 * xác thực hai lớp của Cài đặt bảo mật): badge ở màn người dùng phải tải lại.
 */
export function useKhiDoiBatBuocMfa() {
  const queryClient = useQueryClient();
  return (moi: MfaRequiredUsers) => {
    queryClient.setQueryData(KHOA_NGUOI_DUNG.batBuocMfa, moi);
    void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.danhSach });
    void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.tatCa });
  };
}

/**
 * Ghi hồ sơ mới vào **mọi** danh sách đang có trong cache — dòng đổi ngay tại
 * chỗ, không tải lại cả mấy trang đã nối — rồi đánh dấu cũ để lần mở bộ lọc
 * sau tải lại cho đúng (người vừa khoá phải rời bộ lọc "Đang hoạt động").
 */
function useGhiXong() {
  const queryClient = useQueryClient();
  return (moi: UserProfile) => {
    queryClient.setQueriesData<InfiniteData<UserPage>>(
      { queryKey: KHOA_NGUOI_DUNG.danhSach },
      (cu) =>
        cu && {
          ...cu,
          pages: cu.pages.map((p) => ({
            ...p,
            items: p.items.map((x) => (x.user_id === moi.user_id ? moi : x)),
          })),
        },
    );
    queryClient.setQueryData<UserProfile[]>(KHOA_NGUOI_DUNG.tatCa, (cu) =>
      cu?.map((x) => (x.user_id === moi.user_id ? moi : x)),
    );
    void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.danhSach, refetchType: 'none' });
  };
}

/** `409`: trạng thái vừa bị quản trị khác đổi — tải lại để nhãn và menu nói đúng. */
function useKhiXungDot() {
  const queryClient = useQueryClient();
  return (err: unknown) => {
    if ((err as ApiError | undefined)?.status === 409) {
      void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.danhSach });
    }
  };
}

/** Sửa tên hiển thị. Lỗi không bật toast: form hiện lỗi ngay trong hộp. */
export function useSuaNguoiDung() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  return useMutation<
    UserProfile,
    ApiError,
    { id: string; du: Pick<UserUpdate, 'display_name' | 'department_id'> }
  >({
    mutationFn: ({ id, du }) => nguoiDungRepository.sua(id, du),
    onSuccess: (moi, { du }) => {
      ghiXong(moi);
      // Số người của phòng ban cũ và mới ở màn Cơ cấu tổ chức đã đổi.
      if (du.department_id !== undefined) void queryClient.invalidateQueries({ queryKey: KHOA_PHONG_BAN.goc });
      toast.success(t('nguoiDung.daLuu', { ten: moi.display_name }));
    },
  });
}

/**
 * Bật hoặc bỏ cờ bắt buộc xác thực hai lớp riêng của một người (`PATCH` với
 * `mfa_required`). Bật đi qua hộp xác nhận nên lỗi hiện trong hộp; bỏ bấm thẳng
 * từ menu nên lỗi hiện bằng toast — phía gọi lo.
 */
export function useBatBuocMfaNguoiDung() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  return useMutation<UserProfile, ApiError, { id: string; batBuoc: boolean }>({
    mutationFn: ({ id, batBuoc }) => nguoiDungRepository.sua(id, { mfa_required: batBuoc }),
    onSuccess: (moi, { batBuoc }) => {
      ghiXong(moi);
      // Tập cờ riêng đổi thì mức của tổ chức có thể đổi theo (người cuối cùng bị bỏ).
      void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.batBuocMfa });
      toast.success(
        t(batBuoc ? 'nguoiDung.mfa.daBatBuoc' : 'nguoiDung.mfa.daBoBatBuoc', { ten: moi.display_name }),
      );
    },
  });
}

export type HanhDongKhoa = 'khoa' | 'mo-khoa';

/**
 * Khoá hoặc mở khoá (`PATCH` với `status`). Khoá đi qua hộp xác nhận nên lỗi
 * hiện trong hộp; mở khoá bấm thẳng từ menu nên lỗi hiện bằng toast.
 */
export function useKhoaNguoiDung() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const khiXungDot = useKhiXungDot();
  return useMutation<UserProfile, ApiError, { id: string; hanhDong: HanhDongKhoa }>({
    mutationFn: ({ id, hanhDong }) =>
      nguoiDungRepository.sua(id, { status: hanhDong === 'khoa' ? 'DISABLED' : 'ACTIVE' }),
    onSuccess: (moi, { hanhDong }) => {
      ghiXong(moi);
      toast.success(
        t(hanhDong === 'khoa' ? 'nguoiDung.daKhoa' : 'nguoiDung.daMoKhoa', { ten: moi.display_name }),
      );
    },
    onError: khiXungDot,
  });
}

/**
 * Gán vai trò lớp 2 cho người vừa mời (`GET` lấy `ETag` rồi `PUT` với
 * `If-Match`, ADR-0004). API mời chưa nhận vai trò lớp 2, nên đây là lệnh thứ
 * hai. Gộp với tập server đang có thay vì ghi đè: lần thử lại sau một lần
 * `PUT` đã tới server không làm mất gì.
 */
async function ganVaiTroChoNguoiMoi(userId: string, roleIds: string[]) {
  const hienCo = await vaiTroRepository.vaiTroCuaNguoi(userId);
  const tap = [...new Set([...hienCo.role_ids, ...roleIds])];
  return vaiTroRepository.ganVaiTroChoNguoi(userId, tap, hienCo.etag);
}

/** Kết quả mời: người dùng đã tạo, kèm lỗi của bước gán vai trò nếu bước đó hỏng. */
export interface KetQuaMoi {
  moi: InvitedUser;
  /** `null` khi không có vai trò nào để gán hoặc gán xong. */
  loiGanVaiTro: ApiError | null;
}

/**
 * Mời người dùng, rồi gán vai trò lớp 2 đã chọn (nếu có). `khoa`
 * (`Idempotency-Key`) do form giữ: sinh mới mỗi lần mở hộp, giữ nguyên khi gửi
 * lại đúng nội dung cũ — mất mạng, bấm hai lần không tạo hai lời mời.
 *
 * Mời hỏng thì mutation lỗi (form hiện lỗi). Mời xong mà gán vai trò hỏng thì
 * mutation **vẫn thành công**: người dùng đã có, chỉ chưa có vai trò —
 * `loiGanVaiTro` cho phía gọi báo và mời gán lại (`useGanVaiTroNguoiMoi`).
 * Quản trị tổ chức không gán vai trò lớp 2: đã toàn quyền (ADR-0077).
 */
export function useMoiNguoiDung() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<KetQuaMoi, ApiError, { du: UserInvite; khoa: string; roleIds: string[] }>({
    mutationFn: async ({ du, khoa, roleIds }) => {
      const moi = await nguoiDungRepository.moi(du, khoa);
      if (du.platform_role === 'TENANT_ADMIN' || roleIds.length === 0) return { moi, loiGanVaiTro: null };
      try {
        const kq = await ganVaiTroChoNguoiMoi(moi.user_id, roleIds);
        queryClient.setQueryData(KHOA_VAI_TRO.cuaNguoi(moi.user_id), kq);
        return { moi, loiGanVaiTro: null };
      } catch (err) {
        return { moi, loiGanVaiTro: err as ApiError };
      }
    },
    onSuccess: ({ moi }, { roleIds }) => {
      void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.danhSach });
      if (moi.department_id) void queryClient.invalidateQueries({ queryKey: KHOA_PHONG_BAN.goc });
      // Số người của từng vai trò ở màn Cấu hình vai trò đã đổi.
      if (roleIds.length > 0) void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
      // `invite_sent_at` null: đã tạo người dùng nhưng email chưa đi (ADR-0006 mục 3).
      // Thư mời đi tới email nhận thư (ADR-0101); người dùng được thêm theo tên đăng nhập.
      if (moi.invite_sent_at) toast.success(t('nguoiDung.daGuiLoiMoi', { email: moi.email_nhan_thu ?? moi.email }));
      else toast.warning(t('nguoiDung.daTaoChuaGuiEmail', { email: moi.email }));
    },
  });
}

/**
 * Gán lại vai trò cho người vừa mời khi lần gán ngay sau lời mời hỏng (nút
 * trên toast cảnh báo). Toast kết quả do phía gọi lo: hỏng lần nữa thì phải
 * hiện lại nút thử.
 */
export function useGanVaiTroNguoiMoi() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { userId: string; roleIds: string[] }>({
    mutationFn: async ({ userId, roleIds }) => {
      const kq = await ganVaiTroChoNguoiMoi(userId, roleIds);
      queryClient.setQueryData(KHOA_VAI_TRO.cuaNguoi(userId), kq);
      return kq;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.danhSach });
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
    },
  });
}

/**
 * Gửi lại lời mời — bấm thẳng từ menu, không có hộp nào để đặt lỗi, nên mọi
 * kết quả đi bằng toast. Hết lượt trong ngày (`429`) dùng câu riêng: câu chung
 * "quá nhiều yêu cầu, đợi vài giây" là sai với hạn mức theo ngày.
 */
export function useGuiLaiLoiMoi() {
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  const khiXungDot = useKhiXungDot();
  return useMutation<InviteResent, ApiError, UserProfile>({
    mutationFn: (u) => nguoiDungRepository.guiLaiLoiMoi(u.user_id),
    onSuccess: (kq, u) => {
      const nguoiNhan = noiNhanThu(u) ?? u.display_name;
      toast.success(
        kq.resends_left_today > 0
          ? t('nguoiDung.daGuiLaiLoiMoi', { email: nguoiNhan, so: kq.resends_left_today })
          : t('nguoiDung.daGuiLaiLoiMoiHetLuot', { email: nguoiNhan }),
      );
    },
    onError: (err) => {
      khiXungDot(err);
      toast.error(
        maLoi(err) === 'RATE_LIMITED' && err.status === 429
          ? t('nguoiDung.hetLuotGuiLai')
          : dichLoi(err),
      );
    },
  });
}

/*
 * ─────────────── Lấy lại tài khoản ───────────────
 *
 * Ba endpoint không trả hồ sơ (`202`/`204`): hook tự ghi trường vừa đổi vào
 * dòng đang có trong cache, để nhãn và menu đổi ngay mà không tải lại cả bảng.
 */

/**
 * Gửi email đặt lại mật khẩu — đi qua hộp xác nhận, nên lỗi hiện **trong**
 * hộp (phía gọi đọc `error`). `409` tải lại danh sách: trạng thái vừa bị đổi.
 */
export function useGuiDatLaiMatKhau() {
  const toast = useToast();
  const { t } = useT();
  const khiXungDot = useKhiXungDot();
  return useMutation<void, ApiError, UserProfile>({
    mutationFn: (u) => nguoiDungRepository.guiDatLaiMatKhau(u.user_id),
    onSuccess: (_kq, u) => {
      toast.success(t('nguoiDung.layLai.daGuiDatLai', { email: noiNhanThu(u) ?? u.display_name }));
    },
    onError: khiXungDot,
  });
}

/** Gỡ khoá tạm — bấm thẳng từ menu; lỗi đi bằng toast, phía gọi lo. */
export function useMoKhoaDangNhap() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  return useMutation<void, ApiError, UserProfile>({
    mutationFn: (u) => nguoiDungRepository.moKhoaDangNhap(u.user_id),
    onSuccess: (_kq, u) => {
      ghiXong({ ...u, login_locked: false });
      toast.success(t('nguoiDung.layLai.daMoKhoaDangNhap', { ten: u.display_name }));
    },
  });
}

/** Đặt lại xác thực hai lớp — đi qua hộp xác nhận, lỗi hiện trong hộp. */
export function useDatLaiMfa() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  return useMutation<void, ApiError, UserProfile>({
    mutationFn: (u) => nguoiDungRepository.datLaiMfa(u.user_id),
    onSuccess: (_kq, u) => {
      ghiXong({ ...u, mfa_configured: false });
      toast.success(t('nguoiDung.layLai.daDatLaiMfa', { ten: u.display_name }));
    },
  });
}

/**
 * Đổi email nhận thư (ADR-0101) — đi qua hộp có ô nhập, nên lỗi hiện trong hộp
 * (phía gọi đọc `error`). Đổi xong ghi địa chỉ mới vào dòng đang có; thư xác
 * minh không gửi được thì vẫn đã đổi, báo bằng toast cảnh báo.
 */
export function useDoiEmailNhanThu() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const { tenantId } = useTenantCuaToi();
  return useMutation<EmailNhanThuChanged, ApiError, { nguoi: UserProfile; email: string }>({
    mutationFn: ({ nguoi, email }) =>
      nguoiDungRepository.doiEmailNhanThu(tenantId ?? '', nguoi.user_id, email.trim()),
    onSuccess: (kq, { nguoi }) => {
      if (!kq.changed) return;
      ghiXong({ ...nguoi, email_nhan_thu: kq.email_nhan_thu });
      if (kq.verify_email_sent) toast.success(t('nguoiDung.daDoiEmailNhanThu', { ten: nguoi.display_name }));
      else toast.warning(t('nguoiDung.daDoiChuaGuiXacMinh', { ten: nguoi.display_name }));
    },
  });
}
